import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const id = href.replace("#", "");
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // On another page (e.g. /projekte): go home, then scroll via hash
      navigate(`/${href}`);
    }
  };

  const handleNav = (l) => {
    setOpen(false);
    if (l.route) {
      navigate(l.href);
      window.scrollTo({ top: 0 });
      return;
    }
    go(l.href);
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 border-b"
      style={{
        borderColor: scrolled
          ? "var(--lx-border-strong)"
          : "var(--lx-border)",
        backgroundColor: scrolled
          ? "rgba(11,12,14,0.85)"
          : "rgba(11,12,14,0.55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        transition: "background-color .3s ease, border-color .3s ease",
      }}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <button
          data-testid="navbar-logo"
          onClick={goHome}
          className="flex items-center group"
          aria-label="LiberoX Labs — zum Seitenanfang"
        >
          <img
            src="/liberox-labs-alpha.png"
            alt="LiberoX Labs"
            className="h-9 w-auto transition-transform duration-300 group-hover:scale-[1.03] sm:h-10"
            style={{ filter: "drop-shadow(0 0 10px rgba(168,232,92,0.25))" }}
          />
        </button>

        <div
          data-testid="navbar-navigation"
          className="hidden items-center gap-8 md:flex"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNav(l)}
              className="lx-navlink font-display text-sm"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:block">
          <Button
            data-testid="navbar-primary-cta"
            onClick={() => go("#kontakt")}
            className="rounded-[12px] font-display font-medium"
            style={{
              background: "var(--lx-green)",
              color: "var(--lx-canvas)",
            }}
          >
            Projekt anfragen
          </Button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="grid h-10 w-10 place-items-center rounded-[10px] border md:hidden"
          style={{ borderColor: "var(--lx-border)" }}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t md:hidden"
            style={{
              borderColor: "var(--lx-border)",
              backgroundColor: "rgba(11,12,14,0.97)",
            }}
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l)}
                  className="rounded-[10px] px-3 py-3 text-left font-display text-[15px] text-[color:var(--lx-text-2)] hover:bg-[color:var(--lx-surface)] hover:text-[color:var(--lx-text)]"
                >
                  {l.label}
                </button>
              ))}
              <Button
                onClick={() => go("#kontakt")}
                className="mt-2 rounded-[12px] font-display font-medium"
                style={{
                  background: "var(--lx-green)",
                  color: "var(--lx-canvas)",
                }}
              >
                Projekt anfragen
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
